import { Field, Form as FormikForm, FormikValues } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import * as yup from "yup";
import { useLayerService } from "../../../hooks";
import { WfsGetFeatureRequest } from "../../../types/Wfs/WfsGetFeatureRequest";
import { useDataSelectionContext } from "../DataSelectionContext";
import DataSelectionModel from "../DataSelectionModal";
import ThemedSelect from "../../../@ui/ThemedSelect";
import { Dataset } from "../../../types/UsCensus/Dataset";
import { SubDataset } from "../../../types/UsCensus/SubDataset";
import { Variable } from "../../../types/UsCensus/Variable";
import LoadingSpinner from "../../Loading/LoadingSpinner";

export interface CensusFormData {
  outputType: number;
  dataset: Dataset | null;
  subDataset: SubDataset | null;
  variableGroup: VariableGroup | null;
  selectedVariables: Variable[];
  spatialLevel: number;
  lastSelectedVariable: Variable | null;
}

interface VariableGroup {
  group: string;
  concept: string;
  variables: Variable[];
}

const UsCensus = () => {
  const layerName = "us:census";
  const layerService = useLayerService();
  const { layers, getLayer, wfsOrder } = useDataSelectionContext();
  const layer = useMemo(() => getLayer(layerName), [layers]);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const initialValues = useMemo(
    () =>
      ({
        outputType: 2,
        dataset: null,
        subDataset: null,
        selectedVariables: [],
        spatialLevel: 0,
        variableGroup: null,
        lastSelectedVariable: null,
      }) as CensusFormData,
    [layer],
  );

  const schema = useMemo(
    () =>
      yup.object().shape({
        subDataset: yup.object().required(),
        dataset: yup.object().required(),
        outputType: yup.number().required(),
        selectedVariables: yup
          .array()
          .of(yup.object()) // Explicitly validate array of objects
          .min(1, "At least one variable must be selected")
          .required("Variables selection is required"),
      }),
    [layer],
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      layerService
        .GetUsCensusDatasets()
        .then((datasets) => {
          setDatasets(datasets);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    };

    fetchInitialData();
  }, [layerService]);

  const onSubmit = (values: CensusFormData) => {
    if (values.dataset === null) {
      return;
    }

    if (values.selectedVariables.length === 0) {
      return;
    }

    if (values.spatialLevel === null || values.spatialLevel === undefined) {
      return;
    }

    let filter = "Dataset = " + values.dataset.datasetId;
    filter +=
      " AND VariableId IN " +
      values.selectedVariables.map((v) => v.variableId).join(",");
    filter += " AND SpatialLevel = " + values.spatialLevel;

    wfsOrder({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      resultType: "Results",
      typeNames: layerName,
      outputFormat:
        values.outputType === 1 ? "gml"
        : values.outputType === 2 ? "csv"
        : "shapefile",
      filter: filter,
    } as WfsGetFeatureRequest);
  };

  const GetVariableGroups = (values: FormikValues & CensusFormData) => {
    if (values.subDataset === null || values.subDataset === undefined) {
      return [];
    }

    var variableGroups: VariableGroup[] = [];
    values.subDataset.variables.forEach((variable: Variable) => {
      const group = variable.group;
      const concept = variable.concept;

      // Find the group in the variableGroups array
      let groupEntry = variableGroups.find(
        (g) => g.group === group && g.concept === concept,
      );
      if (!groupEntry) {
        // If not found, create a new group entry
        groupEntry = { group: group, concept: concept, variables: [] };
        variableGroups.push(groupEntry);
      }
      // Add the variable to the group's variables
      groupEntry.variables.push(variable);
    });

    variableGroups.forEach((group) => {
      // Sort the by label within each group
      group.variables.sort((a: Variable, b: Variable) => {
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
      });
    });

    return variableGroups.sort((a, b) => {
      if (a.concept < b.concept) return -1;
      if (a.concept > b.concept) return 1;
      return 0;
    });
  };

  const CleanVariableLabel = (label: string) => {
    label = label.replaceAll("!!", " ");
    if (label.endsWith(":")) {
      label = label.trim().slice(0, -1);
    }

    label = label.replaceAll(": ", " -> ");

    return label.trim();
  };

  return (
    <>
      <DataSelectionModel
        headerText="US Census"
        initialValues={initialValues}
        size="lg"
        onSubmit={onSubmit}
        layer={layer}
        validationSchema={schema}
      >
        {({ values, errors, setFieldValue }) => (
          <FormikForm>
            <Row className="justify-content-center">
              <Col className="col-md-10">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">Output Type</Form.Label>
                  <Field
                    as={Form.Select}
                    name="outputType"
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setFieldValue("outputType", parseInt(e.target.value));
                    }}
                  >
                    {[
                      { value: 1, label: "GML" },
                      { value: 2, label: "CSV" },
                      { value: 3, label: "ShapeFile" },
                    ].map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Field>
                </Form.Group>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col className="col-md-10">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">
                    Geographical Area Type
                  </Form.Label>
                  <div>
                    <Form.Control
                      as="select"
                      name="spatialLevel"
                      onChange={(e) => {
                        const selectedSpatialLevel = e.target.value;
                        setFieldValue("spatialLevel", selectedSpatialLevel);
                        // Reset dependent fields
                        setFieldValue("selectedVariables", []);
                        setFieldValue("variableGroup", null);
                        setFieldValue("subDataset", null);
                        setFieldValue("dataset", null);
                      }}
                    >
                      <option value="">Select Spatial Level</option>
                      <option value="0">Block</option>
                      <option value="1">Block Group</option>
                      <option value="2">Tract</option>
                    </Form.Control>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col className="col-md-10">
                <Form.Group>
                  <Form.Label className="mb-0 mt-2">Dataset</Form.Label>
                  <div>
                    {!isLoading && (
                      <Form.Control
                        as="select"
                        name="dataset"
                        value={values.dataset?.datasetId || ""}
                        onChange={(e) => {
                          const selectedDatasetId = e.target.value;
                          if (selectedDatasetId) {
                            const dataset = datasets.find(
                              (x) =>
                                x.datasetId.toString() === selectedDatasetId,
                            );
                            if (dataset) {
                              setFieldValue("dataset", dataset);
                            } else {
                              setFieldValue("dataset", null);
                            }
                          } else {
                            setFieldValue("dataset", null);
                          }
                          // Reset dependent fields
                          setFieldValue("subDataset", null);
                          setFieldValue("selectedVariables", []);
                          setFieldValue("variableGroup", null);
                        }}
                      >
                        <option value="">Select a dataset</option>
                        {datasets.map((dataset) => (
                          <option
                            key={dataset.datasetId}
                            value={dataset.datasetId}
                          >
                            {dataset.name} {dataset.vintage}
                          </option>
                        ))}
                      </Form.Control>
                    )}
                    {isLoading && <LoadingSpinner />}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {values.dataset && (
              <Row className="justify-content-center">
                <Col className="col-md-10">
                  <Form.Group>
                    <Form.Label>Sub Dataset</Form.Label>
                    <ThemedSelect
                      options={(() => {
                        let categoryOptions = values.dataset.subDatasets.map(
                          (subDataset: SubDataset) => ({
                            value: subDataset.subDatasetId.toString(),
                            label: subDataset.name,
                            isDisabled:
                              values.spatialLevel <
                              subDataset.usCensusSpatialLevel,
                          }),
                        );

                        categoryOptions.unshift({
                          value: "",
                          label: "Select a sub dataset",
                          isDisabled: false,
                        });

                        return categoryOptions;
                      })()}
                      isLoading={isLoading}
                      isDisabled={isLoading}
                      value={
                        values.subDataset ?
                          {
                            value: values.subDataset.subDatasetId.toString(),
                            label: values.subDataset.name,
                          }
                        : null
                      }
                      onChange={(e: any) => {
                        const selectedSubDataset = e;
                        if (selectedSubDataset) {
                          const d = values.dataset?.subDatasets.find(
                            (x: { subDatasetId: number }) =>
                              x.subDatasetId.toString() ===
                              selectedSubDataset.value,
                          );
                          if (d) {
                            setFieldValue("subDataset", d);
                          } else {
                            setFieldValue("subDataset", null);
                          }
                        } else {
                          setFieldValue("subDataset", null);
                        }

                        // Reset dependent fields
                        setFieldValue("variableGroup", null);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {values.subDataset && (
              <Row className="justify-content-center">
                <Col className="col-md-10">
                  <Form.Group>
                    <Form.Label>Variable Group</Form.Label>
                    <ThemedSelect
                      options={GetVariableGroups(values).map(
                        (v: VariableGroup) => ({
                          value: v.group,
                          label: `${v.concept} (${v.variables.length})`,
                        }),
                      )}
                      value={
                        values.variableGroup ?
                          {
                            value: values.variableGroup.group,
                            label: `${values.variableGroup.concept} (${values.variableGroup.variables.length})`,
                          }
                        : null
                      }
                      onChange={(e: any) => {
                        let group =
                          GetVariableGroups(values).find(
                            (v: VariableGroup) => v.group === e.value,
                          ) || null;
                        setFieldValue("variableGroup", group);
                        setFieldValue("lastSelectedVariable", null);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {values.variableGroup &&
              values.variableGroup.variables &&
              values.variableGroup.variables.length > 0 && (
                <Row className="justify-content-center">
                  <Col className="col-md-10">
                    <Form.Group>
                      <Form.Label>Variables</Form.Label>
                      <ThemedSelect
                        options={values.variableGroup.variables.map(
                          (v: Variable) => ({
                            value: v.variableId.toString(),
                            label: CleanVariableLabel(v.label),
                            isDisabled: values.selectedVariables.some(
                              (selected: Variable) =>
                                selected.variableId === v.variableId,
                            ),
                          }),
                        )}
                        value={
                          values.lastSelectedVariable ?
                            {
                              value:
                                values.lastSelectedVariable.variableId.toString(),
                              label: CleanVariableLabel(
                                values.lastSelectedVariable.label,
                              ),
                            }
                          : null
                        }
                        onChange={(e: any) => {
                          const selectedVariable =
                            values.variableGroup?.variables.find(
                              (v: Variable) =>
                                v.variableId.toString() === e.value,
                            );
                          if (selectedVariable) {
                            // Check if the variable is already selected
                            const isAlreadySelected =
                              values.selectedVariables.some(
                                (v) =>
                                  v.variableId === selectedVariable.variableId,
                              );
                            if (!isAlreadySelected) {
                              setFieldValue("selectedVariables", [
                                ...values.selectedVariables,
                                selectedVariable,
                              ]);
                              setFieldValue(
                                "lastSelectedVariable",
                                selectedVariable,
                              );
                            }
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

            {values.selectedVariables &&
              values.selectedVariables.length > 0 && (
                <Row className="justify-content-center">
                  <Col className="col-md-10">
                    <p className="mb-0 mt-2">Selected Variables</p>
                    <div
                      className="border 
            rounded 
            m-auto
            d-flex 
            flex-wrap 
            gap-2
            p-3
            justify-content-start 
            align-items-start"
                      style={{ maxHeight: 120, overflowY: "scroll" }}
                    >
                      {values.selectedVariables.map((column, index) => (
                        <Button
                          key={index}
                          variant="outline-info"
                          size="sm"
                          style={{ textAlign: "left" }}
                          title={CleanVariableLabel(column.label)}
                          onClick={() => {
                            const updatedColumns =
                              values.selectedVariables.filter(
                                (c) => c.variableId !== column.variableId,
                              );
                            setFieldValue("selectedVariables", updatedColumns);
                          }}
                        >
                          <b>{column.name}</b>
                        </Button>
                      ))}
                      {values.selectedVariables.length === 0 && (
                        <p className="text-muted p-0 m-0">
                          No columns selected
                        </p>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
          </FormikForm>
        )}
      </DataSelectionModel>
    </>
  );
};

export default UsCensus;
